/**
 * Column utilities for TAS Programs reporting.
 *
 * Pure functions for field-option grouping/sorting.
 */
import type { FieldOption } from './opensearch-field-mapper';
import { stripTypeSuffixes, disambiguateColumnLabels } from './label-generation';

/**
 * Canonical display-order for field groups in the column picker.
 * "Admin Configuration" is deliberately last.
 */
const GROUP_SORT_ORDER = [
  'Employee',
  'Employment',
  'Contact Information',
  'Postal Address',
  'Identifiers',
  'Program Instance',
  'Program Template',
  'Application',
  'Applicant',
  'Limit',
  'Eligibility',
  'Evidence',
  'Digital Assets & Attachments',
  'Review & Queue',
  'Courses',
  'Participant',
  'LNP / Learning Partner',
  'Withdrawal',
  'Payments',
  'Scholarships',
  'Assistance',
  'Benefit Year',
  'Company Settings',
  'PSLF',
  'SYF',
  'SLR',
  'Form',
  'Borrower Fields',
  'Employer Fields',
  'Plan Settings & Assignments',
  'Borrower',
  'Organization',
  'Loans',
  'Match Data',
  'Contribution',
  'Company',
  'Other',
  'Admin Configuration', // Always at the bottom
];

/**
 * Group an array of `FieldOption`s by their `.group` property, then sort the
 * groups according to `GROUP_SORT_ORDER`.  Within a base group, " / Custom"
 * subgroups sort after their parent.
 */
export function groupFieldOptions(fields: FieldOption[]): Array<[string, FieldOption[]]> {
  // Disambiguate labels before grouping so the field picker never shows
  // multiple identical labels (e.g. six "Employee ID" entries from different
  // OS field paths that all strip down to the same display label).
  const disambiguated = disambiguateColumnLabels(fields, { availableFields: fields });

  const groups = new Map<string, FieldOption[]>();

  for (const field of disambiguated) {
    const group = field.group || 'Other';
    let list = groups.get(group);
    if (!list) {
      list = [];
      groups.set(group, list);
    }
    list.push(field);
  }

  const groupedArray = Array.from(groups.entries());

  const customSuffix = ' / Custom';

  const parseGroupSortMeta = (groupName: string) => {
    const isCustomSubgroup = groupName.endsWith(customSuffix);
    const baseGroup = isCustomSubgroup
      ? groupName.slice(0, groupName.length - customSuffix.length)
      : groupName;
    return { baseGroup, isCustomSubgroup };
  };

  const getGroupPosition = (baseGroup: string) => {
    // "Admin Configuration" is always last, regardless of other groups.
    if (baseGroup === 'Admin Configuration') {
      return Number.POSITIVE_INFINITY;
    }

    const index = GROUP_SORT_ORDER.indexOf(baseGroup);

    // Groups not in the order list go after known non-admin groups,
    // but still before "Admin Configuration".
    return index === -1 ? GROUP_SORT_ORDER.length : index;
  };

  return groupedArray.sort((a, b) => {
    const aMeta = parseGroupSortMeta(a[0]);
    const bMeta = parseGroupSortMeta(b[0]);
    const aPosition = getGroupPosition(aMeta.baseGroup);
    const bPosition = getGroupPosition(bMeta.baseGroup);
    if (aPosition !== bPosition) {
      return aPosition - bPosition;
    }

    // Within same base group: base group first, then Custom subgroup
    if (aMeta.isCustomSubgroup !== bMeta.isCustomSubgroup) {
      return aMeta.isCustomSubgroup ? 1 : -1;
    }

    return a[0].localeCompare(b[0]);
  });
}

/**
 * Normalize a field path to the form used by `pruneSelectedColumnsByResolvedSource`:
 * strip type suffixes, drop `_` and `.`, lowercase, and treat
 * `allEmployee...` containers as equivalent to `employee...` (the backend
 * canonicalizes between `all_employee_custom_fields_flattened.*` and
 * `employee_custom_fields_flattened.*`).
 *
 * Exported only for unit tests; production callers should go through
 * `pruneSelectedColumnsByResolvedSource`.
 */
export function normalizeFieldPathForResolvedSource(path: string): string {
  const normalized = stripTypeSuffixes(path).replace(/[_.]/g, '').toLowerCase();
  return normalized.replace(/^allemployee/, 'employee');
}

/**
 * Build a normalized index of resolved source paths, including every dotted
 * prefix so that a parent container key (e.g. `employee_ef_fields.employee_custom_fields`)
 * matches when the backend resolved only leaf paths beneath it.
 */
function buildResolvedSourceIndex(resolvedSource: readonly string[]): Set<string> {
  const index = new Set<string>();
  for (const path of resolvedSource) {
    index.add(normalizeFieldPathForResolvedSource(path));

    const segments = path.split('.');
    for (let i = 1; i < segments.length; i++) {
      index.add(normalizeFieldPathForResolvedSource(segments.slice(0, i).join('.')));
    }
  }
  return index;
}

/**
 * Check whether a field key matches the resolved source index.
 * Tries the key itself, then each of its dotted prefixes.
 */
function matchesResolvedSource(fieldKey: string, index: Set<string>): boolean {
  if (index.has(normalizeFieldPathForResolvedSource(fieldKey))) {
    return true;
  }

  const segments = fieldKey.split('.');
  for (let i = 1; i < segments.length; i++) {
    if (index.has(normalizeFieldPathForResolvedSource(segments.slice(0, i).join('.')))) {
      return true;
    }
  }
  return false;
}

/**
 * Filter a list of field options to only those whose key appears in the
 * backend's resolvedSource.  Uses the same normalization as
 * `pruneSelectedColumnsByResolvedSource` so the two stay in sync.
 *
 * When `resolvedSource` is empty/undefined the full list is returned
 * (before the first search we don't know which fields have data).
 */
export function filterFieldsByResolvedSource<T extends { key: string }>(
  fields: readonly T[],
  resolvedSource: readonly string[] | undefined
): T[] {
  if (!Array.isArray(resolvedSource) || resolvedSource.length === 0) {
    return fields.slice();
  }

  const index = buildResolvedSourceIndex(resolvedSource);
  return fields.filter((field) => matchesResolvedSource(field.key, index));
}

/**
 * Drop selected columns whose canonical path the backend did not recognize
 * for the most recent search. The backend already expands wildcards and
 * coerces `_keyword`/dotted-container aliases when it builds
 * `field_meta.resolvedSource`, so any selectedColumn whose normalized key
 * is absent from that set would render blank.
 *
 * Matching uses normalizeFieldPathForResolvedSource (suffix-stripped +
 * separator-stripped + lowercased + `allEmployee` → `employee`), plus a
 * dotted-prefix check so a container-level selection like
 * `employee_ef_fields.employee_custom_fields` still matches when the
 * backend resolved leaf paths beneath it.
 *
 * Returns the kept subset; if `current.length === 0` or `resolvedSource`
 * is empty/undefined, returns `current` unchanged (defensive — the caller
 * should treat "no resolvedSource" as "unknown, do nothing").
 *
 * @example
 * pruneSelectedColumnsByResolvedSource(
 *   ['all_employee.first_name', 'company_name'],
 *   ['employee.firstName']
 * );
 * // => ['all_employee.first_name']
 */
export function pruneSelectedColumnsByResolvedSource(
  current: readonly string[],
  resolvedSource: readonly string[] | undefined
): string[] {
  if (!Array.isArray(resolvedSource) || resolvedSource.length === 0 || current.length === 0) {
    return current.slice();
  }

  const index = buildResolvedSourceIndex(resolvedSource);
  return current.filter((column) => matchesResolvedSource(column, index));
}

/**
 * Move `movedKey` within `keys` so it lands immediately before `beforeKey`,
 * or at the end when `beforeKey` is `null`.
 *
 * Keyed rather than positional on purpose. The selected-columns UI renders the
 * subset of the stored key order that resolves against the loaded field
 * catalog, so a saved key the catalog no longer knows about is invisible in
 * that list. A position taken from the rendered list therefore addresses a
 * different entry in the stored order, and reordering by index moves a column
 * the author never touched.
 *
 * Returns a copy of `keys` unchanged when `movedKey` is absent, when
 * `beforeKey` is a non-null key that is absent, or when `beforeKey` equals
 * `movedKey`.
 *
 * @example
 * moveColumnKeyBefore(['a', 'ghost', 'b', 'c'], 'c', 'b');
 * // => ['a', 'ghost', 'c', 'b']
 */
export function moveColumnKeyBefore(
  keys: readonly string[],
  movedKey: string,
  beforeKey: string | null
): string[] {
  const remaining = keys.filter((key) => key !== movedKey);
  if (remaining.length === keys.length) return keys.slice();

  const target = beforeKey === null ? remaining.length : remaining.indexOf(beforeKey);
  if (target === -1) return keys.slice();

  remaining.splice(target, 0, movedKey);
  return remaining;
}
